import { useState } from 'react';
import {
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { EmptyContent } from 'src/components/empty-content';
import {
  RESCAN_STATUS_CHOICES,
  RescanHistoryItem,
  rescanTransaction,
} from '../../../services/relayTransactionRescanService';
import { Resources } from '../../resource';
import './init';

const RESOURCE = Resources.xbitRelayTransactionRescan;

const CHAIN_CHOICES = [
  { id: 1, name: '1 — eth' },
  { id: 56, name: '56 — bsc' },
  { id: 137, name: '137 — pol' },
  { id: 8453, name: '8453 — base' },
  { id: 42161, name: '42161 — arb' },
  { id: 728126428, name: '728126428 — tron' },
];

// ── Status chip helpers ───────────────────────────────────────────────────────

const statusColor = (status: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'PENDING':
      return 'info';
    case 'RPC_FAILED':
    case 'PARSE_FAILED':
    case 'PUBLISH_FAILED':
      return 'error';
    default:
      return 'default';
  }
};

// ── Rescan Dialog ─────────────────────────────────────────────────────────────

const RescanDialog = ({
  record,
  onClose,
}: {
  record: Partial<RescanHistoryItem>;
  onClose: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [txHash, setTxHash] = useState(record.txHash ?? '');
  const [chainId, setChainId] = useState(String(record.chainId ?? ''));
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await rescanTransaction({
        tx_hash: txHash,
        chain_id: Number(chainId),
        timestamp: Math.floor(Date.now() / 1000),
      });
      notify(t('module.xbitRelayTransactionRescan.rescanSuccess', { _: 'Rescan submitted' }), {
        type: 'success',
        autoHideDuration: 1500,
      });
      onClose();
      refresh();
    } catch (err: any) {
      notify(
        err?.message ?? t('module.xbitRelayTransactionRescan.rescanFailed', { _: 'Rescan failed' }),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('module.xbitRelayTransactionRescan.rescanDialogTitle', { _: 'Rescan Transaction' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('module.xbitRelayTransactionRescan.txHash', { _: 'Tx Hash' })}
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <FormControl size="small" fullWidth required>
            <InputLabel>
              {t('module.xbitRelayTransactionRescan.chainId', { _: 'Chain ID' })}
            </InputLabel>
            <Select
              value={chainId}
              label={t('module.xbitRelayTransactionRescan.chainId', { _: 'Chain ID' })}
              onChange={(e) => setChainId(String(e.target.value))}
            >
              {CHAIN_CHOICES.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !txHash || !chainId}
        >
          {t('module.xbitRelayTransactionRescan.rescan', { _: 'Rescan' })}
        </Button>
      </DialogActions>
    </>
  );
};

// ── Toolbar ───────────────────────────────────────────────────────────────────

const ListActions = ({ onScanTx }: { onScanTx: () => void }) => {
  const t = useTranslate();
  return (
    <TopToolbar
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 2,
          flexWrap: 'wrap',
          '& form': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
          },
          '& .ra-input': { mt: 0, mb: 0 },
          '& .MuiFormControl-root': { mt: 0, mb: 0 },
          '& .MuiInputBase-root': { minHeight: 40 },
        }}
      >
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={onScanTx}
          sx={{ mb: '4px', px: 2 }}
        >
          {t('module.xbitRelayTransactionRescan.scanTx', { _: 'Scan Tx' })}
        </Button>
        <FilterForm
          filters={[
            <TextInput
              key="txHash"
              source="txHash"
              label="module.xbitRelayTransactionRescan.txHash"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 280 }}
            />,
            <SelectInput
              key="chainId"
              source="chainId"
              label="module.xbitRelayTransactionRescan.chainId"
              choices={CHAIN_CHOICES}
              alwaysOn
              resettable
              emptyText="actions.all"
              size="small"
              sx={{ minWidth: 180 }}
            />,
            <SelectInput
              key="status"
              source="status"
              label="module.xbitRelayTransactionRescan.status"
              choices={RESCAN_STATUS_CHOICES.map((s) => ({ id: s, name: s }))}
              alwaysOn
              resettable
              emptyText="actions.all"
              size="small"
              sx={{ minWidth: 160 }}
            />,
          ]}
        />
      </Box>
    </TopToolbar>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const TransactionRescanList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [rescanRecord, setRescanRecord] = useState<Partial<RescanHistoryItem> | null>(null);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied', { type: 'info' });
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={t('module.xbitRelayTransactionRescan.title', { _: 'Transaction Rescan' })}
        links={[
          { name: t('resource.xbitRelay'), href: '#' },
          { name: t('module.xbitRelayTransactionRescan.title') },
        ]}
      />

      <Card sx={{ mt: 3, pb: 2 }}>
        <List
          resource={RESOURCE}
          actions={<ListActions onScanTx={() => setRescanRecord({})} />}
          empty={false}
          perPage={20}
          exporter={false}
          pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
        >
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              empty={<EmptyContent />}
              sx={{
                '& .RaDatagrid-headerCell': { fontWeight: 600 },
                '& .RaDatagrid-rowCell': { py: 1.25 },
              }}
            >
              <FunctionField
                label="module.xbitRelayTransactionRescan.createdAt"
                sortable={false}
                render={(r: any) =>
                  r.createdAtUnixMs ? (
                    <Tooltip
                      title={dayjs(Number(r.createdAtUnixMs)).format('YYYY-MM-DD HH:mm:ss')}
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                      >
                        {dayjs(Number(r.createdAtUnixMs)).format('MM-DD HH:mm:ss')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    '-'
                  )
                }
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.txHash"
                sortable={false}
                render={(r: any) =>
                  r.txHash ? (
                    <Tooltip title={r.txHash} arrow>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => copy(r.txHash)}
                      >
                        {`${r.txHash.slice(0, 10)}…${r.txHash.slice(-6)}`}
                      </Typography>
                    </Tooltip>
                  ) : (
                    '-'
                  )
                }
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.chainId"
                sortable={false}
                render={(r: any) => (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {r.chainId}
                  </Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.status"
                sortable={false}
                render={(r: any) => (
                  <Chip
                    label={r.status || '-'}
                    size="small"
                    color={statusColor(r.status)}
                    variant={r.status === 'PENDING' ? 'outlined' : 'filled'}
                  />
                )}
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.errorMsg"
                sortable={false}
                render={(r: any) =>
                  r.errorMsg ? (
                    <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-line' }}>
                      {r.errorMsg}
                    </Typography>
                  ) : (
                    '-'
                  )
                }
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.blockNumber"
                sortable={false}
                render={(r: any) => (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {r.blockNumber ?? '-'}
                  </Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayTransactionRescan.logCount"
                sortable={false}
                render={(r: any) => r.logCount ?? '-'}
              />
              {/* <FunctionField
                label="common.actions"
                sortable={false}
                render={(r: any) => (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setRescanRecord(r)}
                    >
                      {t('module.xbitRelayTransactionRescan.rescan', { _: 'Rescan' })}
                    </Button>
                  </Stack>
                )}
              /> */}
            </Datagrid>
          </Box>
        </List>
      </Card>

      <Dialog
        open={Boolean(rescanRecord)}
        onClose={() => setRescanRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        {rescanRecord && (
          <RescanDialog record={rescanRecord} onClose={() => setRescanRecord(null)} />
        )}
      </Dialog>
    </DashboardContent>
  );
};

export default TransactionRescanList;
